import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { dataFetch } from '../../api';

const { FiMessageSquare, FiPlus, FiSend, FiTrash2, FiLoader } = FiIcons;

const AdminNotes = ({ learnerId }) => {
  const [notes, setNotes] = useState([]);
  const [input, setInput] = useState('');
  const [showInput, setShowInput] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (learnerId) {
      fetchNotes();
    }
  }, [learnerId]);

  const fetchNotes = async () => {
    setLoading(true);
    const userId = localStorage.getItem('user_id');
    const res = await dataFetch(`read/notes?employee_id=${learnerId}&owner_id=${userId}`);
    if (res?.status === 'success') {
      // Sort notes by created_at descending
      const sortedNotes = (res.data || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setNotes(sortedNotes);
    }
    setLoading(false);
  };

  const getFormattedDate = () => {
    // Converts "2024-05-01T12:34:56.789Z" to "2024-05-01 12:34:56"
    return new Date().toISOString().replace('T', ' ').substring(0, 19);
  };

  const addNote = async () => {
    if (!input.trim() || saving) return;
    setSaving(true);
    
    const userId = localStorage.getItem('user_id');
    const newNote = {
      employee_id: String(learnerId),
      notes_details: input.trim(),
      created_at: getFormattedDate(),
      updated_at: getFormattedDate(),
      user_id: userId
    };

    const res = await dataFetch(`create/notes?owner_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(newNote)
    });

    if (res?.status === 'success') {
      setInput('');
      setShowInput(false);
      fetchNotes();
    } else {
      console.error(res?.error || "Error creating record");
      alert(res?.error || "Failed to save note. Please try again.");
    }
    setSaving(false);
  };

  const deleteNote = async (noteId) => {
    const userId = localStorage.getItem('user_id');
    const res = await dataFetch(`delete/notes/${noteId}?owner_id=${userId}`, {
      method: 'DELETE'
    });
    if (res?.status === 'success') {
      fetchNotes();
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    if (isNaN(d)) return isoString;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <SafeIcon icon={FiMessageSquare} className="text-blue-500" /> Notes
        </h3>
        <button 
          onClick={() => setShowInput(s => !s)} 
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
        >
          <SafeIcon icon={FiPlus} /> Add
        </button>
      </div>

      <AnimatePresence>
        {showInput && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mb-3">
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Add an admin note…"
              rows={2}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:border-blue-500 bg-slate-50"
              disabled={saving}
            />
            <div className="flex justify-end gap-2 mt-1.5">
              <button 
                onClick={() => setShowInput(false)} 
                className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1"
                disabled={saving}
              >
                Cancel
              </button>
              <button 
                onClick={addNote} 
                disabled={saving || !input.trim()}
                className="flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSend} />} 
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-2">
        {loading ? (
          <div className="flex justify-center py-4">
            <SafeIcon icon={FiLoader} className="animate-spin text-slate-400 text-xl" />
          </div>
        ) : notes.length === 0 ? (
          <p className="text-xs text-slate-400">No notes yet.</p>
        ) : (
          notes.map(n => (
            <div key={n.id} className="p-3 bg-amber-50 border border-amber-100 rounded-lg group">
              <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">{n.notes_details}</p>
              <div className="flex items-center justify-between mt-1.5">
                <p className="text-xs text-slate-400">
                  Admin · {formatDate(n.created_at)}
                </p>
                <button 
                  onClick={() => deleteNote(n.id)} 
                  className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-400 transition-all"
                  title="Delete note"
                >
                  <SafeIcon icon={FiTrash2} className="text-xs" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminNotes;